/**
 * Module 'aijobs': AI work on requirements, run through Claude Code on one of your machines
 * with your own login (headless `claude -p`, structured JSON output against a strict schema;
 * no API key). Every run is a visible, cancellable job with its cost; what it returns becomes
 * a proposal a person reviews before anything is created (module 'missions').
 *
 * This file holds the job types, the hub <-> agent messages, a strict JSON validator, and for
 * each task its prompt, its output schema and the step that turns the output into proposed
 * items. Browser-safe and pure: tests run the same code with a fake Claude Code.
 */
import type { Id, Ts } from '../index.ts';
import type { FailureCause } from './failures.ts';
import { ITEM_KINDS, ITEM_KIND_LABEL, PARENT_KINDS, SEVERITIES, criterionText, normalizeKind } from './missions.ts';
import type { AcceptanceInput, BacklogItem, DefectDetails, ItemKind, ItemPriority, LinkType, ProposedItem, Severity, SourceSection } from './missions.ts';

export const AIJOBS_EXT = 'aijobs';

/** Audit labels for the actions this module records. */
export const AIJOBS_AUDIT_LABEL: Record<string, string> = {
  'aijob.start': 'Start AI job',
  'aijob.done': 'AI job finished',
  'aijob.fail': 'AI job failed',
  'aijob.cancel': 'Cancel AI job',
  'aijob.settings': 'Change AI settings',
};

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

/** handoff: a session's handoff written from its transcript (ADR 0062 decision 2a, protocol ext/continuation.ts). */
export type AiTask = 'import' | 'draft' | 'improve' | 'split' | 'criteria' | 'estimate' | 'dedupe' | 'defect' | 'review' | 'handoff';
export const AI_TASKS: readonly AiTask[] = ['import', 'draft', 'improve', 'split', 'criteria', 'estimate', 'dedupe', 'defect', 'review', 'handoff'];
export const AI_TASK_LABEL: Record<AiTask, string> = {
  import: 'Import document',
  draft: 'Draft item',
  improve: 'Improve item',
  split: 'Split story',
  criteria: 'Write acceptance criteria',
  estimate: 'Estimate',
  dedupe: 'Find duplicates',
  defect: 'Draft defect',
  review: 'Review finished work',
  handoff: 'Write handoff',
};

export type AiJobState = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
export const AI_JOB_STATE_LABEL: Record<AiJobState, string> = { queued: 'Queued', running: 'Running', done: 'Done', failed: 'Failed', cancelled: 'Cancelled' };

/** Where a running job is: the hub sends it, Claude Code starts, reads, writes, the hub checks the answer. */
export type AiJobStage = 'sending' | 'starting' | 'reading' | 'writing' | 'checking' | 'finished';
export const AI_JOB_STAGE_LABEL: Record<AiJobStage, string> = {
  sending: 'Sending to the machine',
  starting: 'Starting the agent',
  reading: 'Reading',
  writing: 'Writing',
  checking: 'Checking the answer',
  finished: 'Finished',
};

export interface AiJobError {
  what: string;
  why: string;
  next: string;
}

export interface AiJob {
  id: Id;
  task: AiTask;
  /** One line: "Import spec.md", "Split QAWL-12". */
  title: string;
  projectId: Id | null;
  itemId: Id | null;
  machineId: Id;
  model: string;
  state: AiJobState;
  stage: AiJobStage;
  by: string;
  createdAt: Ts;
  startedAt: Ts | null;
  endedAt: Ts | null;
  /** USD as Claude Code reports it (with a subscription login it is what the run would cost on the API). */
  cost: number | null;
  tokens: { input: number; output: number } | null;
  turns: number | null;
  /** Stream events seen and characters written so far: the progress bar reads them. */
  events: number;
  outputChars: number;
  /** About how many characters the answer will be, for a determinate progress bar. */
  expectedChars: number;
  error: AiJobError | null;
  /**
   * The agent that runs (or ran) it: 'claude-code', 'codex' or 'gemini-cli'. Absent on jobs from
   * hubs before 20 Oct 2026, which ran on Claude Code. Additive.
   */
  agentKind?: string;
  /** The agents that failed it for a reason of their own before the one it ran on, oldest first. Additive. */
  attempts?: AiJobAttempt[];
  /** What the job produced (module 'missions'). */
  proposalId: Id | null;
  /** One line about the result: "12 stories in 3 epics". */
  summary: string | null;
}

/** One agent that could not run a job (signed out, usage limit, not installed): the job moved on. */
export interface AiJobAttempt {
  kind: string;
  machineId: Id;
  cause: FailureCause;
  /** What the agent said, in one line. */
  why: string;
  at: Ts;
}

/**
 * The agents an AI job can run on (20 Oct 2026): Claude Code (`claude -p`), Codex (`codex exec`)
 * and Gemini CLI (`gemini -p`), each headless, read-only and with the machine owner's login.
 */
export const AI_JOB_AGENT_KINDS: readonly string[] = ['claude-code', 'codex', 'gemini-cli'];

export function isAiJobAgentKind(v: unknown): v is string {
  return typeof v === 'string' && AI_JOB_AGENT_KINDS.includes(v);
}

export interface AiJobsSettings {
  /** Model alias or id for every job unless one asks for another. Default sonnet. */
  model: string;
  /**
   * The agent every job runs on, or null (the default) for any agent that is on and has room:
   * Orgus picks one per job and moves to the next when one fails for a reason of its own. Additive.
   */
  agentKind?: string | null;
  /** The machine jobs run on; null picks the hub's own machine, else the first online one. */
  machineId: Id | null;
  /** Claude Code stops a job that would cost more than this (USD). */
  maxBudgetUsd: number;
  /** A job is stopped after this long. */
  timeoutMs: number;
}

// model '' runs the machine's own Claude Code default (works on every account and deployment).
export const AI_DEFAULT_SETTINGS: AiJobsSettings = { model: '', agentKind: null, machineId: null, maxBudgetUsd: 2, timeoutMs: 10 * 60_000 };

/** state.ext.aijobs */
export interface AiJobsState {
  jobs: AiJob[];
  settings: AiJobsSettings;
}

/** POST /api/aijobs/settings */
export type AiJobsSettingsPatch = Partial<AiJobsSettings>;

export const MODEL_RE = /^[A-Za-z0-9._:\-[\]]{1,80}$/;

export function aiSettingsError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the settings as a JSON object.';
  if (v.model !== undefined && (typeof v.model !== 'string' || (v.model !== '' && !MODEL_RE.test(v.model)))) return 'The model must be an alias such as sonnet, opus or haiku, or a model id.';
  if (v.agentKind !== undefined && v.agentKind !== null && !isAiJobAgentKind(v.agentKind)) return 'The agent must be claude-code, codex or gemini-cli, or null for any agent that is on.';
  if (v.machineId !== undefined && v.machineId !== null && typeof v.machineId !== 'string') return 'The machine must be a machine id, or null to pick one.';
  if (v.maxBudgetUsd !== undefined && (typeof v.maxBudgetUsd !== 'number' || !(v.maxBudgetUsd > 0) || v.maxBudgetUsd > 100)) return 'The budget per job must be more than $0 and at most $100.';
  if (v.timeoutMs !== undefined && (typeof v.timeoutMs !== 'number' || v.timeoutMs < 30_000 || v.timeoutMs > 60 * 60_000)) return 'The time limit must be between 30 seconds and 1 hour.';
  return null;
}

const STAGE_FLOOR: Record<AiJobStage, number> = { sending: 0.02, starting: 0.06, reading: 0.12, writing: 0.2, checking: 0.95, finished: 1 };

/**
 * A determinate progress fraction for a job: the stage sets a floor; while Claude Code thinks,
 * the stream events it sends move the bar up to half way; while it writes, the characters
 * written against the expected size fill the rest. It never goes back, and never past 0.9
 * before the answer is checked.
 */
export function jobProgress(job: Pick<AiJob, 'state' | 'stage' | 'outputChars' | 'expectedChars'> & Partial<Pick<AiJob, 'events'>>): number {
  if (job.state === 'done' || job.state === 'failed' || job.state === 'cancelled') return 1;
  const floor = STAGE_FLOOR[job.stage];
  if (job.stage !== 'writing' && job.stage !== 'reading') return floor;
  const expected = Math.max(1, job.expectedChars);
  const thinking = Math.min(1, (job.events ?? 0) / Math.max(50, expected / 4));
  const written = Math.min(1, job.outputChars / expected);
  return Math.min(0.9, Math.max(floor, 0.12 + 0.4 * thinking, 0.2 + 0.7 * written));
}

export function isJobLive(s: AiJobState): boolean {
  return s === 'queued' || s === 'running';
}

// ---------------------------------------------------------------------------
// Hub <-> agent messages ({ t: 'ext', ext: 'aijobs', type, id?, data })
// ---------------------------------------------------------------------------

/** hub -> agent 'run'. The agent answers 'accepted' at once, then 'progress' and one 'result'. */
export interface AiRunRequest {
  jobId: Id;
  prompt: string;
  /** The output schema (passed to claude --json-schema). */
  schema: StrictSchema;
  model: string;
  /** A folder of files the job may read (attachments the hub delivered), relative to the agent's attachments folder. */
  folder?: string;
  maxBudgetUsd: number;
  timeoutMs: number;
  /**
   * The agent to run it on: 'claude-code' (also when absent), 'codex' or 'gemini-cli'. An agent
   * that cannot run that kind answers a result with `cause: 'launch'`. Additive, 20 Oct 2026.
   */
  agentKind?: string;
  /**
   * Claude Code only: ask the hub for this job's launch grant first (module 'policy', ticketed by
   * the job id), so it runs on the workspace's launch account (AssistantRunRequest.grant). Additive, 24 Oct 2026.
   */
  grant?: { accountId: string | null };
}

export interface AiRunProgress {
  jobId: Id;
  stage: AiJobStage;
  events: number;
  outputChars: number;
}

export interface AiRunResult {
  jobId: Id;
  ok: boolean;
  /** The structured output, when Claude Code returned one. */
  output?: unknown;
  /** The final text (the answer when structured output was not available). */
  text?: string;
  cost?: number;
  usage?: { input: number; output: number };
  turns?: number;
  durationMs: number;
  error?: string;
  cancelled?: boolean;
  /**
   * Why it failed, when the agent knows it was the agent's own fault (not installed, signed out,
   * usage limit): the hub then moves the job to the next agent. Absent: the hub reads `error`. Additive.
   */
  cause?: FailureCause;
  /**
   * The machine's agent has no runner for this kind (a capability it lacks, not a failure of the
   * agent): the hub moves the job on and never strikes that agent for it. Additive, 21 Oct 2026.
   */
  unsupported?: boolean;
}

/** hub -> agent 'cancel' */
export interface AiRunCancel {
  jobId: Id;
}

// ---------------------------------------------------------------------------
// Strict JSON schemas and their validator
// ---------------------------------------------------------------------------

/** The JSON Schema subset the output schemas use: every object closed, every field required. */
export interface StrictSchema {
  type: 'object' | 'array' | 'string' | 'integer' | 'number' | 'boolean';
  description?: string;
  properties?: Record<string, StrictSchema>;
  required?: string[];
  additionalProperties?: false;
  items?: StrictSchema;
  enum?: readonly (string | number)[];
  maxItems?: number;
  minItems?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The first way `value` breaks `schema`, as "items[2].title must be text", or null. */
export function schemaError(schema: StrictSchema, value: unknown, at = 'answer'): string | null {
  if (schema.enum && !schema.enum.includes(value as string | number)) return `${at} must be one of ${schema.enum.join(', ')}.`;
  switch (schema.type) {
    case 'object': {
      if (!isObj(value)) return `${at} must be an object.`;
      const props = schema.properties ?? {};
      for (const r of schema.required ?? []) if (!(r in value)) return `${at}.${r} is missing.`;
      for (const [k, v] of Object.entries(value)) {
        const p = props[k];
        if (!p) {
          if (schema.additionalProperties === false) return `${at}.${k} is not part of the schema.`;
          continue;
        }
        const e = schemaError(p, v, `${at}.${k}`);
        if (e) return e;
      }
      return null;
    }
    case 'array': {
      if (!Array.isArray(value)) return `${at} must be a list.`;
      if (schema.maxItems !== undefined && value.length > schema.maxItems) return `${at} has ${value.length} entries; at most ${schema.maxItems} are allowed.`;
      if (schema.minItems !== undefined && value.length < schema.minItems) return `${at} needs at least ${schema.minItems} entries.`;
      if (schema.items) {
        for (let i = 0; i < value.length; i++) {
          const e = schemaError(schema.items, value[i], `${at}[${i}]`);
          if (e) return e;
        }
      }
      return null;
    }
    case 'string':
      if (typeof value !== 'string') return `${at} must be text.`;
      if (schema.maxLength !== undefined && value.length > schema.maxLength) return `${at} is longer than ${schema.maxLength} characters.`;
      return null;
    case 'integer':
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) return `${at} must be a number.`;
      if (schema.type === 'integer' && !Number.isInteger(value)) return `${at} must be a whole number.`;
      if (schema.minimum !== undefined && value < schema.minimum) return `${at} must be at least ${schema.minimum}.`;
      if (schema.maximum !== undefined && value > schema.maximum) return `${at} must be at most ${schema.maximum}.`;
      return null;
    case 'boolean':
      return typeof value === 'boolean' ? null : `${at} must be true or false.`;
  }
}

/**
 * The JSON in a text answer: the whole text, or the first fenced ```json block, or the span
 * from the first { to the last }. Null when none parses.
 */
export function extractJson(text: string): unknown {
  const tries: string[] = [text.trim()];
  const fence = /```(?:json)?\s*\n([\s\S]*?)```/i.exec(text);
  if (fence?.[1]) tries.push(fence[1].trim());
  const a = text.indexOf('{');
  const b = text.lastIndexOf('}');
  if (a >= 0 && b > a) tries.push(text.slice(a, b + 1));
  for (const t of tries) {
    try {
      return JSON.parse(t) as unknown;
    } catch {
      /* next */
    }
  }
  return null;
}

const str = (maxLength: number, description?: string): StrictSchema => ({ type: 'string', maxLength, ...(description ? { description } : {}) });
const obj = (properties: Record<string, StrictSchema>, description?: string): StrictSchema => ({
  type: 'object',
  additionalProperties: false,
  required: Object.keys(properties),
  properties,
  ...(description ? { description } : {}),
});
const arr = (items: StrictSchema, maxItems: number, description?: string): StrictSchema => ({ type: 'array', items, maxItems, ...(description ? { description } : {}) });

export const PRIORITY_WORDS = ['urgent', 'high', 'medium', 'low'] as const;
type PriorityWord = (typeof PRIORITY_WORDS)[number];

const CRITERION_SCHEMA = obj(
  {
    format: { type: 'string', enum: ['gwt', 'check'], description: 'gwt for Given/When/Then, check for a plain checklist line.' },
    given: str(400, 'The starting state (gwt), else empty.'),
    when: str(400, 'The action (gwt), else empty.'),
    then: str(400, 'The observable outcome (gwt), else empty.'),
    text: str(400, 'The checklist line (check), else empty.'),
  },
  'One acceptance criterion a test or a reviewer can check.',
);

const DEFECT_SCHEMA = obj(
  {
    severity: { type: 'string', enum: [...SEVERITIES] },
    environment: str(1000, 'Where it happens: OS, browser, device, version. Empty if unknown.'),
    steps: str(3000, 'Numbered steps to reproduce, one per line. Empty if unknown.'),
    expected: str(1500, 'What should happen.'),
    actual: str(1500, 'What happens instead.'),
  },
  'Defect details. For anything that is not a defect: severity minor and empty strings.',
);

export const ITEM_SCHEMA = obj({
  ref: str(20, 'A short unique reference within this answer: E1, F1, S1, T1, D1.'),
  parent_ref: str(20, 'The ref of its parent in this answer, or empty for none.'),
  kind: { type: 'string', enum: [...ITEM_KINDS] },
  title: str(200, 'Short and specific. Stories read as an outcome.'),
  description: str(6000, 'Markdown. Stories use "As a <role>, I want <goal>, so that <benefit>." then context. A flow, sequence, state machine or data model that is clearer drawn than told goes in a ```mermaid fence (flowchart, sequenceDiagram, stateDiagram-v2, erDiagram), drawn in the console: solid arrows for control flow labelled with the decision, dotted for events, and e1@{ animate: true } on a data-flow edge so it moves; subgraph per system.'),
  acceptance_criteria: arr(CRITERION_SCHEMA, 12),
  priority: { type: 'string', enum: [...PRIORITY_WORDS] },
  estimate: { type: 'integer', minimum: 0, maximum: 100, description: 'Story points on 1, 2, 3, 5, 8, 13; 0 when it does not apply (epics, features).' },
  labels: arr(str(40), 6),
  source_sections: arr(str(20), 12, 'Ids of the document sections it comes from, such as S3.2. Empty when there is no document.'),
  defect: DEFECT_SCHEMA,
  note: str(600, 'One sentence: why this item, or what is unclear about it.'),
});

/** The output schema of each task. */
export const AI_SCHEMAS: Record<AiTask, StrictSchema> = {
  import: obj({
    items: arr(ITEM_SCHEMA, 150, 'Epics, features, stories (and tasks only where a story clearly needs them), parents before children.'),
    open_questions: arr(obj({ question: str(500), source_sections: arr(str(20), 12) }), 40, 'What the document leaves open and someone must decide.'),
    assumptions: arr(str(500), 40, 'What you assumed where the document is silent.'),
  }),
  draft: obj({ items: arr(ITEM_SCHEMA, 6, 'Usually one item; a few only when the request clearly holds several.') }),
  defect: obj({ items: arr(ITEM_SCHEMA, 1) }),
  improve: obj({
    title: str(200),
    description: str(6000),
    acceptance_criteria: arr(CRITERION_SCHEMA, 12),
    defect: DEFECT_SCHEMA,
    rationale: str(800, 'What you changed and why, in one or two sentences.'),
  }),
  split: obj({ items: arr(ITEM_SCHEMA, 8, 'The smaller stories that replace the original. Each delivers something on its own (INVEST).'), rationale: str(800) }),
  criteria: obj({ acceptance_criteria: arr(CRITERION_SCHEMA, 12), rationale: str(800) }),
  estimate: obj({
    estimate: { type: 'integer', minimum: 0, maximum: 100, description: 'Story points: 1, 2, 3, 5, 8, 13 or 21.' },
    confidence: { type: 'string', enum: ['low', 'medium', 'high'] },
    rationale: str(800),
  }),
  dedupe: obj({ pairs: arr(obj({ a: str(30, 'Key of one item.'), b: str(30, 'Key of the item it duplicates.'), reason: str(400) }), 50) }),
  // ORGU-85 (review.ts): one verdict per acceptance criterion of a finished item.
  review: obj({
    criteria: arr(
      obj({
        id: str(10, 'The criterion id as listed: AC-1, AC-2, ...'),
        verdict: { type: 'string', enum: ['met', 'not_met', 'not_checkable'] },
        evidence: str(300, 'One line: the test, file or commit that shows it, or what is missing, or why it cannot be checked.'),
      }),
      30,
    ),
    summary: str(600, 'One or two sentences on the change as a whole.'),
  }),
  handoff: obj({
    goal: str(600, 'What the session set out to do, in one or two sentences.'),
    done: arr(str(300), 20, 'What is finished and verified (committed, tests passing), most important first.'),
    next: arr(str(300), 12, 'The next steps in order, each one concrete enough to start on.'),
    files: arr(str(300), 30, 'Files that matter for the next steps: the path, then why in a few words.'),
    decisions: arr(str(400), 15, 'Decisions made and constraints learned that a new session must keep.'),
    open_questions: arr(str(400), 10, 'What is still unclear or waits on a person.'),
  }),
};

// ---------------------------------------------------------------------------
// Prompts
// ---------------------------------------------------------------------------

const RULES = [
  'You are a careful product analyst turning requirements into a backlog.',
  'Answer only with JSON that matches the schema you were given. Do not use tools unless you are told files are attached. Do not ask questions: put open points in the answer.',
  'Write plain, specific English. Avoid untestable words such as fast, easy, user-friendly, robust, intuitive, appropriate, etc; say what can be observed or measured instead.',
  'Acceptance criteria must be testable: prefer Given/When/Then with an observable Then. A good story is Independent, Negotiable, Valuable, Estimable, Small and Testable.',
  'Kinds: epic (a large outcome), feature (a capability inside an epic), story (one deliverable slice of user value), task (technical work inside a story), defect (something broken), spike (a time-boxed investigation), chore (upkeep).',
  'Hierarchy: epic > feature > story > task. Defects and spikes may sit under an epic or feature.',
];

function itemContext(item: ContextItem): string {
  const lines = [`${ITEM_KIND_LABEL[item.kind]} ${item.key}: ${item.title}`];
  if (item.body.trim()) lines.push(`Description:\n${item.body.trim()}`);
  if (item.acceptance.length) lines.push(`Acceptance criteria:\n${item.acceptance.map((c, i) => `AC-${i + 1}: ${criterionText(c)}`).join('\n')}`);
  if (item.defect) {
    const d = item.defect;
    lines.push(`Defect: severity ${d.severity}. Environment: ${d.environment || 'unknown'}. Steps: ${d.steps || 'unknown'}. Expected: ${d.expected || 'unknown'}. Actual: ${d.actual || 'unknown'}.`);
  }
  if (item.estimate != null) lines.push(`Estimate: ${item.estimate} points.`);
  return lines.join('\n');
}

export type ContextItem = Pick<BacklogItem, 'key' | 'kind' | 'title' | 'body' | 'acceptance' | 'defect' | 'estimate'>;

export interface PromptFile {
  name: string;
  /** Path on the machine the job runs on. */
  path: string;
  mime: string;
}

function filesBlock(files: readonly PromptFile[] | undefined): string {
  if (!files?.length) return '';
  return [
    '',
    'Attached files (read them with the Read tool; images are screenshots):',
    ...files.map((f) => `- ${f.name} (${f.mime}): ${f.path}`),
  ].join('\n');
}

/** Characters per section of output an import writes, roughly, for the progress bar. */
const IMPORT_CHARS_PER_SECTION = 900;

export interface ImportPromptInput {
  project: string;
  document: string;
  sections: readonly SourceSection[];
  /** Existing epics and features to fit new work under, by key. */
  existing?: readonly Pick<BacklogItem, 'key' | 'kind' | 'title'>[];
  guidance?: string;
}

export function importPrompt(p: ImportPromptInput): string {
  const lines = [
    ...RULES,
    '',
    `Task: read the requirements document "${p.document}" for the project "${p.project}" and propose a backlog: epics, features and stories with acceptance criteria. Add tasks only where a story clearly needs separate technical work.`,
    'Give each item the ids of the sections it comes from in source_sections, so every requirement traces back to the document. Every section that states a requirement should be covered by at least one story.',
    'List what the document leaves open as open_questions, and what you assumed as assumptions. Do not invent requirements the document does not support; ask instead.',
    'Order items parents first. Use refs like E1, F1, S1 and point parent_ref at the parent.',
  ];
  if (p.existing?.length) {
    lines.push('', 'The backlog already has these epics and features. Do not repeat them; you may mention their key in a note:', ...p.existing.slice(0, 60).map((e) => `- ${e.key} ${ITEM_KIND_LABEL[e.kind]}: ${e.title}`));
  }
  if (p.guidance?.trim()) lines.push('', `Guidance from the person importing: ${p.guidance.trim()}`);
  lines.push('', '--- DOCUMENT START ---');
  for (const s of p.sections) lines.push('', `[${s.id}] ${s.title}`, s.text);
  lines.push('', '--- DOCUMENT END ---');
  return lines.join('\n');
}

export function importExpectedChars(sections: readonly SourceSection[]): number {
  return Math.max(2000, Math.min(120_000, sections.length * IMPORT_CHARS_PER_SECTION));
}

export interface DraftPromptInput {
  project: string;
  /** What the person typed. */
  request: string;
  /** A kind they chose, if any. */
  kind?: ItemKind | null;
  parent?: ContextItem | null;
  files?: readonly PromptFile[];
}

export function draftPrompt(p: DraftPromptInput): string {
  const lines = [
    ...RULES,
    '',
    `Task: write a complete backlog item for the project "${p.project}" from this request:`,
    `"""${p.request.trim()}"""`,
    p.kind ? `It should be a ${ITEM_KIND_LABEL[p.kind].toLowerCase()}.` : 'Choose the right kind. A screenshot or log showing something broken is a defect.',
    'For a defect fill defect.steps, expected and actual from what the request and files show, and give a severity. For a story write the user story, context and acceptance criteria.',
    'Leave parent_ref empty.',
  ];
  if (p.parent) lines.push('', 'It sits under:', itemContext(p.parent));
  lines.push(filesBlock(p.files));
  return lines.join('\n');
}

export interface DefectPromptInput {
  project: string;
  /** What failed, in one line. */
  title: string;
  kind: string;
  evidence: string;
  /** Items that may be affected, for the description. */
  related?: readonly Pick<BacklogItem, 'key' | 'title'>[];
}

export function defectPrompt(p: DefectPromptInput): string {
  return [
    ...RULES,
    '',
    `Task: write one defect for the project "${p.project}" from this runtime failure (${p.kind}): ${p.title}`,
    'Use the evidence to fill steps, expected, actual and environment. Say in the description what the evidence shows and what is likely wrong; do not guess beyond it. Leave parent_ref empty.',
    ...(p.related?.length ? ['', 'Items it may affect:', ...p.related.slice(0, 20).map((r) => `- ${r.key}: ${r.title}`)] : []),
    '',
    '--- EVIDENCE START ---',
    p.evidence.slice(0, 20_000),
    '--- EVIDENCE END ---',
  ].join('\n');
}

export function improvePrompt(item: ContextItem, files?: readonly PromptFile[]): string {
  return [
    ...RULES,
    '',
    'Task: improve this item without changing what it asks for. Make the title specific, the description clear (a user story for stories), replace untestable wording, and make every acceptance criterion testable. Keep criteria that are already good, worded as they are.',
    item.kind === 'defect' ? 'It is a defect: complete the defect fields where the text supports it.' : 'It is not a defect: set defect.severity to minor and leave the other defect fields empty.',
    '',
    itemContext(item),
    filesBlock(files),
  ].join('\n');
}

export function splitPrompt(item: ContextItem): string {
  return [
    ...RULES,
    '',
    'Task: this story is too large for one piece of work. Split it into 2 to 6 smaller stories that each deliver value on their own (INVEST): split by workflow step, by business rule, by data variation or by interface, not by technical layer. Give each its own acceptance criteria and estimate. Together they must cover every acceptance criterion of the original.',
    'Use kind story, refs S1, S2, ... and leave parent_ref empty.',
    '',
    itemContext(item),
  ].join('\n');
}

export function criteriaPrompt(item: ContextItem): string {
  return [
    ...RULES,
    '',
    'Task: write the acceptance criteria for this item: 3 to 8 testable criteria, Given/When/Then where behaviour is involved. Cover the main path, the important edge cases and errors. Do not repeat criteria it already has; return only new ones.',
    '',
    itemContext(item),
  ].join('\n');
}

export function estimatePrompt(item: ContextItem, reference: readonly Pick<BacklogItem, 'key' | 'title' | 'estimate'>[]): string {
  const ref = reference.filter((r) => r.estimate != null).slice(0, 25);
  return [
    ...RULES,
    '',
    'Task: estimate this item in story points (1, 2, 3, 5, 8, 13, 21) relative to the team\'s other items. Say how confident you are and why in one or two sentences.',
    ...(ref.length ? ['', 'Items already estimated:', ...ref.map((r) => `- ${r.key} (${r.estimate} points): ${r.title}`)] : []),
    '',
    itemContext(item),
  ].join('\n');
}

export function dedupePrompt(items: readonly Pick<BacklogItem, 'key' | 'kind' | 'title' | 'body'>[]): string {
  return [
    ...RULES,
    '',
    'Task: find items below that ask for the same thing in different words (duplicates by meaning, not just similar titles). Return each pair once with the newer one as a and the one it duplicates as b, and say why. Return an empty list if there are none.',
    '',
    ...items.slice(0, 300).map((i) => `- ${i.key} ${ITEM_KIND_LABEL[i.kind]}: ${i.title}${i.body.trim() ? ` | ${i.body.trim().split('\n')[0]!.slice(0, 160)}` : ''}`),
  ].join('\n');
}

// ---------------------------------------------------------------------------
// From the answer to proposed items
// ---------------------------------------------------------------------------

export interface RawCriterion {
  format: 'gwt' | 'check';
  given: string;
  when: string;
  then: string;
  text: string;
}

export interface RawItem {
  ref: string;
  parent_ref: string;
  kind: string;
  title: string;
  description: string;
  acceptance_criteria: RawCriterion[];
  priority: PriorityWord;
  estimate: number;
  labels: string[];
  source_sections: string[];
  defect: { severity: string; environment: string; steps: string; expected: string; actual: string };
  note: string;
}

export function priorityOf(w: string): ItemPriority {
  const i = PRIORITY_WORDS.indexOf(w as PriorityWord);
  return (i < 0 ? 2 : i) as ItemPriority;
}

export function criteriaFrom(raw: readonly RawCriterion[]): AcceptanceInput[] {
  const out: AcceptanceInput[] = [];
  for (const c of raw) {
    if (c.format === 'gwt' && (c.given.trim() || c.when.trim() || c.then.trim())) out.push({ format: 'gwt', text: '', given: c.given.trim(), when: c.when.trim(), then: c.then.trim() });
    else if (c.text.trim()) out.push({ format: 'check', text: c.text.trim() });
    else if (c.then.trim()) out.push({ format: 'check', text: c.then.trim() });
  }
  return out;
}

export function defectFrom(kind: ItemKind, raw: RawItem['defect'] | undefined): DefectDetails | null {
  if (kind !== 'defect' || !raw) return null;
  return {
    severity: SEVERITIES.includes(raw.severity as Severity) ? (raw.severity as Severity) : 'major',
    environment: raw.environment.trim(),
    steps: raw.steps.trim(),
    expected: raw.expected.trim(),
    actual: raw.actual.trim(),
  };
}

/**
 * The AI's items as proposed items: refs made unique, parents that do not exist or cannot hold
 * the kind dropped (the item moves to the top, with a note), sections kept only when the
 * document has them. Everything starts pending.
 */
export function proposedItemsFrom(raw: readonly RawItem[], opts: { sections?: readonly SourceSection[]; parentId?: Id | null; parentKind?: ItemKind | null } = {}): ProposedItem[] {
  const known = new Set((opts.sections ?? []).map((s) => s.id));
  const refs = new Map<string, string>();
  const kinds = new Map<string, ItemKind>();
  const out: ProposedItem[] = [];
  raw.forEach((r, i) => {
    const kind = normalizeKind(r.kind) ?? 'story';
    let ref = (r.ref || '').trim().slice(0, 20) || `R${i + 1}`;
    while ([...refs.values()].includes(ref)) ref = `${ref}x`;
    refs.set(r.ref, ref);
    kinds.set(ref, kind);
    let parentRef = r.parent_ref ? refs.get(r.parent_ref) ?? '' : '';
    let note = r.note?.trim() || undefined;
    if (parentRef && !PARENT_KINDS[kind].includes(kinds.get(parentRef)!)) {
      note = [note, `It was under ${parentRef}, which cannot hold a ${ITEM_KIND_LABEL[kind].toLowerCase()}.`].filter(Boolean).join(' ');
      parentRef = '';
    }
    const parentId = !parentRef && opts.parentId && opts.parentKind && PARENT_KINDS[kind].includes(opts.parentKind) ? opts.parentId : null;
    out.push({
      ref,
      parentRef,
      ...(parentId ? { parentId } : {}),
      kind,
      title: r.title.trim().slice(0, 200) || 'Untitled',
      body: r.description.trim(),
      acceptance: criteriaFrom(r.acceptance_criteria ?? []),
      priority: priorityOf(r.priority),
      estimate: r.estimate > 0 ? r.estimate : null,
      labels: [...new Set((r.labels ?? []).map((l) => l.trim().toLowerCase().slice(0, 40)).filter(Boolean))].slice(0, 6),
      defect: defectFrom(kind, r.defect),
      sections: (r.source_sections ?? []).filter((s) => !known.size || known.has(s)),
      ...(note ? { note } : {}),
      decision: 'pending',
    });
  });
  return out;
}

/** Sections no proposed item traces back to (an import should cover each that states something). */
export function uncoveredSections(sections: readonly SourceSection[], items: readonly Pick<ProposedItem, 'sections' | 'decision'>[]): SourceSection[] {
  const covered = new Set(items.filter((i) => i.decision !== 'rejected').flatMap((i) => i.sections));
  return sections.filter((s) => s.text.trim().length > 40 && !covered.has(s.id) && ![...covered].some((c) => c.startsWith(`${s.id}.`)));
}

/** "1 story", "3 stories". */
export function kindCount(kind: ItemKind, n: number): string {
  const w = ITEM_KIND_LABEL[kind].toLowerCase();
  return `${n} ${n === 1 ? w : w.endsWith('y') ? `${w.slice(0, -1)}ies` : `${w}s`}`;
}

/** What an import or a draft produced, in a few words: "3 epics, 12 stories". */
export function proposalSummary(items: readonly Pick<ProposedItem, 'kind'>[]): string {
  const counts = new Map<ItemKind, number>();
  for (const i of items) counts.set(i.kind, (counts.get(i.kind) ?? 0) + 1);
  const parts = ITEM_KINDS.filter((k) => counts.get(k)).map((k) => kindCount(k, counts.get(k)!));
  return parts.join(', ') || 'Nothing proposed';
}

export interface RawPair {
  a: string;
  b: string;
  reason: string;
}

/** Duplicate pairs by key turned into proposed 'duplicates' links (unknown keys and self-pairs dropped). */
export function duplicateLinksFrom(pairs: readonly RawPair[], byKey: ReadonlyMap<string, Id>): { from: Id; to: Id; type: LinkType; rationale: string }[] {
  const seen = new Set<string>();
  const out: { from: Id; to: Id; type: LinkType; rationale: string }[] = [];
  for (const p of pairs) {
    const a = byKey.get(p.a.trim().toUpperCase());
    const b = byKey.get(p.b.trim().toUpperCase());
    if (!a || !b || a === b) continue;
    const k = [a, b].sort().join('|');
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ from: a, to: b, type: 'duplicates', rationale: p.reason.trim().slice(0, 400) });
  }
  return out;
}
